<template>
  <main class="main main--has-floating-buttons">
    <template v-if="dataLoaded === true">
      <portal to="headerTitle">
        {{ inHouseService.name[hotelSettings.details.primaryLanguage] }}
      </portal>
      <tabable-card cols="12 6@desktop">
        <tabable-card-content :title="$t(`lang.${hotelSettings.details.primaryLanguage}`)">
          <hm-card-inner>
            <hm-input v-model="inHouseService.name[hotelSettings.details.primaryLanguage]"
                      :lazy="true"
                      :disabled="inHouseService.deleted"
                      :label="$t('admin.pageInHouseService.title')"
                      validate="required"
                      name="title"
                      cols="12" />
            <hm-textarea v-model="inHouseService.description[hotelSettings.details.primaryLanguage]"
                         :lazy="true"
                         :disabled="inHouseService.deleted"
                         :label="$t('admin.pageInHouseService.description')"
                         name="description"
                         cols="12" />
          </hm-card-inner>
        </tabable-card-content>
      </tabable-card>
      <tabable-card cols="12 6@desktop">
        <tabable-card-content v-for="lang in hotelSettings.details.additionalLanguages" :title="$t(`lang.${lang}`)" :key="lang">
          <hm-card-inner>
            <hm-input v-model="inHouseService.name[lang]"
                      :lazy="true"
                      :disabled="inHouseService.deleted"
                      :label="$t('admin.pageInHouseService.title')"
                      name="title"
                      cols="12" />
            <hm-textarea v-model="inHouseService.description[lang]"
                         :lazy="true"
                         :disabled="inHouseService.deleted"
                         :label="$t('admin.pageInHouseService.description')"
                         name="description"
                         cols="12" />
          </hm-card-inner>
        </tabable-card-content>
      </tabable-card>

      <card :title="$t('admin.pageInHouseService.card.general')" cols="12">
        <hm-card-inner>
          <hm-input-select id="type"
                           v-model="inHouseService.type"
                           :disabled="inHouseService.deleted"
                           :label="$t('admin.pageInHouseService.type')"
                           name="type"
                           enum-name="InHouseServiceType"
                           cols="12 6@tablet 3@desktop" />
          <hm-multiselect id="applicableTaxes"
                          v-model="inHouseService.applicableTaxes"
                          :options="hotelSettings.taxes.all(inHouseService.applicableTaxes).filter(it => !it.deleted).filter(it => inHouseService.applicableTaxes.includes(it.id) || it.taxType === 'VAT' || (it.taxType === 'Other' && it.percentage))"
                          :disabled="inHouseService.deleted"
                          :label="$t('admin.pageInHouseService.applicableTaxes')"
                          cols="12 6@tablet 3@desktop"
                          name="applicableTaxes"
                          label-expression="${option.name[$shortLocale]}" />
          <hm-input-select id="level"
                           v-model="inHouseService.level"
                           :disabled="inHouseService.deleted || usedAnywhere"
                           :label="$t('admin.pageInHouseService.level')"
                           name="type"
                           enum-name="InHouseServiceRateLevel"
                           cols="12 6@tablet 3@desktop" />
          <hm-input-money v-model="inHouseService.amount"
                          :disabled="inHouseService.deleted"
                          :currency="hotelSettings.details.currency"
                          :digits="2"
                          :lazy="true"
                          :currency-clickable="false"
                          :label="$t('admin.pageInHouseService.amount')"
                          name="amount"
                          cols="12 6@tablet 3@desktop start-1">
            <template slot="icon-inline-right">
              <input-icon-button v-if="inHouseService.rateMode === 'PerRoom'"
                                 :border="true"
                                 :title="$t(`RateMode.${inHouseService.rateMode}`)"
                                 :disabled="inHouseService.deleted || usedForChannelManagerDefaultMapping"
                                 icon="per-room"
                                 @click="inHouseService.rateMode = 'PerPerson'" />
              <input-icon-button v-else-if="inHouseService.rateMode === 'PerPerson'"
                                 :border="true"
                                 :title="$t(`RateMode.${inHouseService.rateMode}`)"
                                 :disabled="inHouseService.deleted || usedForChannelManagerDefaultMapping"
                                 icon="per-person"
                                 @click="inHouseService.rateMode = 'PerRoom'" />
              <input-icon-button v-if="inHouseService.rateFrequency === 'PerStay'"
                                 :border="true"
                                 :title="$t(`RateFrequency.${inHouseService.rateFrequency}`)"
                                 :disabled="inHouseService.deleted || usedForChannelManagerDefaultMapping"
                                 icon="per-stay"
                                 @click="inHouseService.rateFrequency = 'PerNight'" />
              <input-icon-button v-else-if="inHouseService.rateFrequency === 'PerNight'"
                                 :border="true"
                                 :title="$t(`RateFrequency.${inHouseService.rateFrequency}`)"
                                 :disabled="inHouseService.deleted || usedForChannelManagerDefaultMapping"
                                 icon="per-night"
                                 @click="inHouseService.rateFrequency = 'PerStay'" />
            </template>
          </hm-input-money>
          <hm-multiselect v-if="inHouseService.rateMode === 'PerPerson'"
                          id="ageCategories"
                          v-model="inHouseService.ageCategories"
                          :options="hotelSettings.ageCategories.all(inHouseService.ageCategories).filter(it => !it.deleted)"
                          :disabled="inHouseService.deleted"
                          :label="$t('admin.pageInHouseService.ageCategories')"
                          :help="$t('admin.pageInHouseService.ageCategories_help')"
                          cols="12 6@tablet 3@desktop"
                          name="ageCategories"
                          label-expression="${option.name[$shortLocale]}" />
          <hm-input v-if="hasAnyLicense(['LedgerReport', 'DatevReport'])"
                    id="ledgerCode"
                    v-model="inHouseService.ledgerCode"
                    :disabled="inHouseService.deleted"
                    :label="$t('admin.pageInHouseService.ledgerCode')"
                    cols="12 6@tablet 3@desktop"
                    name="ledgerCode" />
        </hm-card-inner>

        <hm-card-inner>
          <hm-input-select id="type"
                           v-model="inHouseService.fiscalizationChargeType"
                           :disabled="inHouseService.deleted || inHouseService.type === 'PrePayment'"
                           :label="$t('admin.pageInHouseService.fiscalizationChargeType')"
                           name="type"
                           enum-name="FiscalizationChargeType"
                           cols="12 6@tablet 3@desktop" />
          <hm-input v-if="hasLicense('FiskaltrustAgencyBusiness')"
                    id="fiscalizationAgencyId"
                    v-model="inHouseService.fiscalizationAgencyId"
                    :disabled="inHouseService.deleted || !hasRole('ROLE_SYSADMIN')"
                    :label="$t('admin.pageInHouseService.fiscalizationAgencyId')"
                    cols="12 6@tablet 3@desktop"
                    name="fiscalizationAgencyId" />
        </hm-card-inner>
      </card>

      <card v-if="usedAnywhere" :title="$t('admin.pageInHouseService.card.usage')" cols="12" card-type="collapsible">
        <hm-card-inner v-if="!isEmpty(usedRatePlans)" :full-size="true" :title="$t('admin.pageInHouseService.card.ratePlans')">
          <hm-col cols="12">
            <template v-for="it in usedRatePlans" v-if="!it.deleted">
              <hm-card-inner-line :title="it.name[$shortLocale]" :clickable="true" :key="it.id" @click="routeToRatePlan(it)" />
            </template>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner v-if="!isEmpty(usedRoomTypes)" :full-size="true" :title="$t('admin.pageInHouseService.card.roomTypes')">
          <hm-col cols="12">
            <template v-for="it in usedRoomTypes" v-if="!it.deleted">
              <hm-card-inner-line :title="it.name[$shortLocale]" :clickable="true" :key="it.id" @click="routeToRoomType(it)" />
            </template>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner v-if="usedConnectCxMapping || usedConnectSMapping" :title="$t('menu.admin.interfaces')" :full-size="true">
          <hm-col cols="12">
            <hm-card-inner-line v-if="usedConnectCxMapping" :clickable="true" title="Connect.CX" @click="$router.push({ name: 'interfaces.connectCX'})" />
            <hm-card-inner-line v-if="usedConnectSMapping" :clickable="true" title="Connect.S" @click="$router.push({ name: 'interfaces.connectS'})" />
          </hm-col>
        </hm-card-inner>
      </card>

      <hm-floating-buttons id="actions" ref="floatingButtons">
        <hm-floating-button v-if="!inHouseService.deleted && canDelete"
                            :label="$t('admin.pageInHouseService.actions.deleteInHouseService')"
                            icon="delete"
                            color="error"
                            @click="deleteInHouseService(false)" />
      </hm-floating-buttons>
    </template>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { cloneDeep, groupBy, isEmpty, isNil, keyBy, sortBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import HmFloatingButton from '../../lib/inputs/HmFloatingButton';
import HmCardInner from '../../lib/layout/HmCardInner';
import HmMultiselect from '../../lib/inputs/HmMultiselect';

export default {
  name: 'PageInHouseService',
  components: { HmMultiselect, HmCardInner, HmFloatingButton },
  autosave: [
    {
      name: 'inHouseService',
      variable: 'inHouseService',
      event: 'hotelSettings.update',
      fn: function (cbSaveComplete) {
        if (this.inHouseService.deleted === true) {
          cbSaveComplete();
          return;
        }

        const toSave = cloneDeep(this.inHouseService);

        this.axios.put(this.serviceUrls.inHouseService.get(this, this.inHouseServiceId), toSave)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();

            this.$serviceBus.$emit('inHouseService.list');
            this.$serviceBus.$emit('hotelSettings.update');
          })
          .catch((error) => {
            if (isNil(error.response)) this.showErrorMessage();
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  mixins: [HotelmeisterMixin, SaveWatchMixin],

  data() {
    return {
      inHouseService: {
        rateMode: null,
        rateFrequency: null,
      },
      inHouseServiceId: this.$route.params.inHouseServiceId,
      dataLoaded: false,
    };
  },

  computed: {
    usedRatePlans: function () {
      return this.hotelSettings.ratePlans
        .filter(it => !it.deleted
          && (
            (!isNil(it.includedInHouseServices) && it.includedInHouseServices.filter(ihs => ihs.inHouseServiceId === this.inHouseService.id).length > 0)
            || (!isNil(it.mandatoryInHouseServices) && it.mandatoryInHouseServices.filter(ihs => ihs.inHouseServiceId === this.inHouseService.id).length > 0)
            || (!isNil(it.bookableInHouseServices) && it.bookableInHouseServices.filter(ihs => ihs.inHouseServiceId === this.inHouseService.id).length > 0)
          ));
    },
    usedRoomTypes: function () {
      return this.hotelSettings.roomTypes
        .filter(it => !it.deleted
          && (
            (!isNil(it.mandatoryInHouseServices) && it.mandatoryInHouseServices.filter(ihs => ihs.inHouseServiceId === this.inHouseService.id).length > 0)
            || (!isNil(it.bookableInHouseServices) && it.bookableInHouseServices.filter(ihs => ihs.inHouseServiceId === this.inHouseService.id).length > 0)
          ));
    },
    usedAnywhere: function () {
      return this.usedRatePlans.length > 0 || this.usedRoomTypes.length > 0 || this.usedConnectCxMapping || this.usedConnectSMapping;
    },
    usedForChannelManagerDefaultMapping: function () {
      return this.usedConnectCxDefaultMapping || this.usedConnectSDefaultMapping;
    },
    usedConnectCxDefaultMapping: function () {
      try {
        return this.hotelSettings.interfaces.connectCx.some(it => it.defaultIhs === this.inHouseServiceId);
      } catch (e) {
        return false;
      }
    },
    usedConnectSDefaultMapping: function () {
      try {
        return this.hotelSettings.interfaces.connectS.defaultIhs === this.inHouseServiceId;
      } catch (e) {
        return false;
      }
    },
    usedConnectCxMapping: function () {
      try {
        return this.usedConnectCxDefaultMapping || this.hotelSettings.interfaces.connectCx.some(itCx => Object.values(itCx.addonMapping).some(it => it === this.inHouseServiceId));
      } catch (e) {
        return false;
      }
    },
    usedConnectSMapping: function () {
      try {
        return this.usedConnectSDefaultMapping || Object.values(this.hotelSettings.interfaces.connectS.addonMapping).some(it => it === this.inHouseServiceId);
      } catch (e) {
        return false;
      }
    },
    canDelete: function () {
      return !this.usedAnywhere;
    },
  },

  watch: {
    inHouseService: function (newVal) {
      if (isNil(newVal)) {
        this.dataLoaded = false;
      }
    },
  },

  created: function () {
    this.loadData();
    this.$serviceBus.$emit('inHouseService.list');
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    routeToRatePlan(ratePlan) {
      this.$router.push({
        name: 'ratePlan',
        params: {
          ratePlanId: ratePlan.id,
        },
      });
    },
    routeToRoomType(roomType) {
      this.$router.push({
        name: 'roomType',
        params: {
          roomTypeId: roomType.id,
        },
      });
    },
    loadData() {
      this.axios.get(this.serviceUrls.inHouseService.get(this, this.inHouseServiceId))
        .then((response) => {
          if (this.dataLoaded === false) {
            this.inHouseService = response.data.data;
            this.dataLoaded = true;
          }
        });
    },
    deleteInHouseService(confirmed = false) {
      if (this.canDelete) {
        if (confirmed === true) {
          this.axios.delete(this.serviceUrls.inHouseService.get(this, this.inHouseServiceId))
            .then((response) => {
              this.dataLoaded = false;
              this.inHouseService = response.data.data;
              this.showSuccessMessage();
              this.$serviceBus.$emit('hotelSettings.update');
              this.$serviceBus.$emit('inHouseService.list');
              this.$router.push({
                name: 'inHouseServices',
              });
            })
            .catch((error) => {
              this.showMessages(error.response);
            })
            .catch((error) => {
              if (isNil(error.response)) this.showErrorMessage();
              this.showMessages(error.response);
            });
        } else {
          this.showModal(
            this.$t('admin.pageInHouseService.confirmDeleteInHouseService'),
            (doNotShowAgain) => {
              this.deleteInHouseService(true);
            },
          );
        }
      } else {
        this.showErrorMessage(this.$t('admin.pageInHouseService.inHouseServiceInUse'));
      }
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PageInHouseService.vue